import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * SuccessFilled component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconSuccessFilled extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M12 23C14.9174 23 17.7153 21.8411 19.7782 19.7782C21.8411 17.7153 23 14.9174 23 12C23 9.08262 21.8411 6.28473 19.7782 4.22183C17.7153 2.15893 14.9174 1 12 1C9.08262 1 6.28473 2.15893 4.22183 4.22183C2.15893 6.28473 1 9.08262 1 12C1 14.9174 2.15893 17.7153 4.22183 19.7782C6.28473 21.8411 9.08262 23 12 23ZM17.5047 9.20129C17.7004 8.95685 17.7909 8.6447 17.7565 8.33349C17.722 8.02229 17.5653 7.73753 17.3209 7.54186C17.0764 7.34618 16.7643 7.25563 16.4531 7.29011C16.1419 7.32459 15.8571 7.48128 15.6614 7.72571L10.0923 14.6934L7.86243 13.0214C7.73861 12.9286 7.59771 12.861 7.44778 12.8226C7.29785 12.7842 7.14183 12.7757 6.98861 12.7976C6.83539 12.8194 6.68799 12.8713 6.55481 12.9502C6.42163 13.029 6.30529 13.1333 6.21243 13.2571C6.11957 13.381 6.052 13.5219 6.01359 13.6718C5.97518 13.8217 5.96667 13.9777 5.98856 14.131C6.01045 14.2842 6.0623 14.4316 6.14115 14.5648C6.22001 14.6979 6.32433 14.8143 6.44814 14.9071L9.591 17.2643C9.83591 17.4483 10.1428 17.5297 10.4467 17.4912C10.7506 17.4528 11.0276 17.2976 11.219 17.0584L17.5047 9.20129Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-success-filled') === undefined) {
  customElements.define('cat-icon-success-filled', BlocksIconSuccessFilled);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-success-filled': BlocksIconSuccessFilled;
  }
}
